import {
  getOrCreateQuickPayLink,
  type PayLink,
} from "../src/api/pay-links";
import { getCurrencyCode, RegionCode } from "./regions";

interface QuickPayLinkOptions {
  paymentMethodId: string;
  region?: RegionCode | null;
  title?: string;
  message?: string;
  amount?: number;
  forceRefresh?: boolean;
}

const quickPayCache = new Map<string, { payLink: PayLink; url: string }>();

function getAppBaseUrl() {
  if (typeof window !== "undefined" && window.location?.origin) {
    return window.location.origin.replace(/\/+$/, "");
  }
  const envBase = (import.meta.env.VITE_PUBLIC_APP_URL as string | undefined)
    ?.trim()
    .replace(/\/+$/, "");
  return envBase ?? "";
}

export function buildPublicPayLinkUrl(token: string) {
  const base = getAppBaseUrl();
  const path = `/pay-links/${token}`;
  return base ? `${base}${path}` : path;
}

export async function createQuickPayLinkForMethod({
  paymentMethodId,
  region,
  title,
  message,
  amount,
  forceRefresh = false,
}: QuickPayLinkOptions) {
  if (!forceRefresh) {
    const cached = quickPayCache.get(paymentMethodId);
    if (cached) {
      if ((cached.payLink.amount ?? 0) <= 0) {
        return cached;
      }
      quickPayCache.delete(paymentMethodId);
    }
  }

  const payLink = await getOrCreateQuickPayLink({
    amount,
    paymentMethodId,
    currency: region ? getCurrencyCode(region) : undefined,
    title: title || "Quick Pay",
    message: message || "Scan to pay instantly.",
    forceNew: forceRefresh,
  });

  const result = {
    payLink,
    url: buildPublicPayLinkUrl(payLink.token),
  };
  if (!forceRefresh && (payLink.amount ?? 0) > 0) {
    return createQuickPayLinkForMethod({
      paymentMethodId,
      region,
      title,
      message,
      amount,
      forceRefresh: true,
    });
  }
  quickPayCache.set(paymentMethodId, result);
  return result;
}

export function invalidateQuickPayLink(paymentMethodId?: string) {
  if (paymentMethodId) {
    quickPayCache.delete(paymentMethodId);
    return;
  }
  quickPayCache.clear();
}
